import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";
import PublicationCard from "@/components/feature/PublicationCard";
import { publicApi } from "@/lib/api";
import type { Publication } from "@/lib/types";
import { SITE_IMAGES } from "@/lib/siteAssets";

const NAVBAR_OFFSET = 112;

function groupByYear(items: Publication[]): [number, Publication[]][] {
  const map = new Map<number, Publication[]>();
  for (const item of items) {
    const list = map.get(item.pubYear) ?? [];
    list.push(item);
    map.set(item.pubYear, list);
  }
  return [...map.entries()].sort((a, b) => b[0] - a[0]);
}

export default function PublicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("search")?.trim() ?? "";

  const [items, setItems] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);

  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollAfterLoadRef = useRef(!!initialQuery);

  const fetchItems = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const suffix = q.trim() ? `?search=${encodeURIComponent(q.trim())}` : "";
      const res = await publicApi.get<Publication[]>(`/public/publications${suffix}`);
      setItems(res.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems(query);
  }, [query, fetchItems]);

  useEffect(() => {
    if (loading || !scrollAfterLoadRef.current) return;

    scrollAfterLoadRef.current = false;
    requestAnimationFrame(() => {
      const el = resultsRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });
  }, [loading, items]);

  const groups = useMemo(() => groupByYear(items), [items]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = search.trim();
    setQuery(trimmed);
    scrollAfterLoadRef.current = true;
    if (trimmed) {
      setSearchParams({ search: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const handleClear = () => {
    setSearch("");
    setQuery("");
    scrollAfterLoadRef.current = false;
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white pt-[96px]">
      <section className="relative h-[240px] md:h-[300px] overflow-hidden">
        <img
          src={SITE_IMAGES.pageBanner}
          alt="Publications"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl md:text-4xl font-light text-white tracking-wide">Publications</h1>
        </div>
      </section>

      <div className="bg-[#f6f9fc] border-b border-gray-100">
        <div className="container mx-auto px-4 py-3">
          <nav className="text-sm text-gray-500">
            <a href="/" className="hover:text-[#1977cc] transition-colors">Home</a>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-[#2c4964]">Publications</span>
          </nav>
        </div>
      </div>

      <section className="container mx-auto px-4 py-10 md:py-14 max-w-4xl">
        <ScrollReveal>
          <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, author, journal..."
              className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
            />
            <button
              type="submit"
              className="px-5 py-2.5 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
            >
              Search
            </button>
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </form>
          {query && !loading && (
            <p className="mt-3 text-sm text-gray-500">
              {items.length > 0
                ? `"${query}" — ${items.length.toLocaleString()} result${items.length === 1 ? "" : "s"}`
                : `"${query}" — no results`}
            </p>
          )}
          </div>
        </ScrollReveal>

        <div ref={resultsRef}>
          {loading ? (
            <p className="text-center text-gray-400 py-16">Loading...</p>
          ) : groups.length === 0 ? (
            <p className="text-center text-gray-400 py-16">No publications found.</p>
          ) : (
            <div className="space-y-10">
              {groups.map(([year, pubs]) => (
                <ScrollReveal key={year}>
                  <h2 className="text-2xl font-semibold text-[#2c4964] mb-4 pb-2 border-b border-gray-200">
                    {year}
                  </h2>
                  <div className="space-y-4">
                    {pubs.map((pub) => (
                      <PublicationCard key={pub.id} publication={pub} searchQuery={query} />
                    ))}
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
